import { FloatButton, type FloatButtonProps, Layout } from 'antd';
import clx from 'classnames';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { down } from 'styled-breakpoints';
import styled from 'styled-components';
import { AffixManagerProvider } from '../affix/AffixManagerContext.tsx';
import { ContentLayoutWrapper } from './ContentLayoutWrapper.tsx';

const { Content, Sider } = Layout;

const HeaderMenuBackgroundColor = styled.div`
  background-color: ${props => props.theme.colorPrimary};
  color: ${props => props.theme.colorWhite};
`;

export interface IPageLayoutBaseProps {
  children: React.ReactNode;
  /**
   * La topBarHeader deve avere l'altezza definita in theme.layoutHeaderHeight
   */
  topBarHeader?: React.ReactNode;
  /**
   * Indica che la header è (Stiky) fissa,
   * l'altezza della barra in alto è definita in theme.layoutHeaderHeight
   */
  fixedHeader?: boolean;
  boxedContent?: boolean;
  noPadding?: boolean;
  className?: string;
  backTopProps?: Omit<FloatButtonProps, 'target'>;
  /**
   * Per l'inserimento di una footer statica (versione del software, ecc.)
   */
  staticFooter?: React.ReactNode;
  /**
   * Footer  della pagina, può essere un componente React
   * meglio se racchiuso in un tag <footer> per accessibilità
   */
  footer?: React.ReactNode;
  /**
   * Elemento da mostrare in cima alla pagina, prima del contenuto principale.
   * Posizionato ne <main> come primo elemento.
   */
  topPage?: React.ReactNode;
  /**
   * Indica se il layout ha un contenuto di tipo tabella
   */
  isTablePage?: boolean;
  paddingInTablePageFullWidth?: boolean;
}

const StyledContent = styled(Content)`
  flex: auto;
  min-height: 0;
  &.has-top-bar.fixed-header {
    margin-top: ${props =>
      props.theme.overrideLayoutHeaderHeightFlag
        ? 0
        : props.theme.layoutHeaderHeight}px;
  }
`;
const StyledLayout = styled(Layout)`
  min-height: 100vh;
`;
const ContentWrapper = styled.div`
  padding: var(--content-padding, 24px);
  ${down('sm')} {
    padding: 0;
  }

  &.no-padding {
    padding: 0;
  }
`;

/**
 * Layout principale dell'applicativo.
 *
 * La topBarHeader deve avere l'altezza definita in theme.layoutHeaderHeight
 *
 */
export function PageLayoutBase(props: IPageLayoutBaseProps) {
  const { t } = useTranslation();

  const hasTopBar = !!props.topBarHeader;

  return (
    <AffixManagerProvider>
      <StyledLayout className={clx(props.className)}>
        {/* Header */}
        {props.topBarHeader}
        {/* Content - Main */}
        <StyledContent
          as="main"
          className={clx({
            'has-top-bar': hasTopBar,
            'fixed-header': props.fixedHeader,
          })}
        >
          {/* Main Top Page */}
          {props.topPage}
          {/* Main - Content */}
          <ContentLayoutWrapper
            boxed={props.boxedContent}
            isTablePage={props.isTablePage}
            paddingInTablePageFullWidth={props.paddingInTablePageFullWidth}
          >
            <ContentWrapper className={clx({ 'no-padding': props.noPadding })}>
              {props.children}
            </ContentWrapper>
          </ContentLayoutWrapper>
        </StyledContent>
        {hasTopBar && (
          <FloatButton.BackTop
            visibilityHeight={200}
            aria-label={t(
              'layout.page-layout-base.top-bar-aria',
              'Vai all’inizio della pagina',
            )}
            {...props.backTopProps}
          />
        )}
        {/* Footer */}
        {props.staticFooter}
        {props.footer}
      </StyledLayout>
    </AffixManagerProvider>
  );
}
